import React, { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "../redux/store";
import { RoleEnum } from "../modules/shared/model/role.enum";
import { checkMaxRevenueLimit, checkMinRevenueThreshold } from "../utils/categoryFormSettingsUtils";
import { fetchLatestCategoryFormSettings } from "../redux/Actions/CategoryFormSettingsAction";

const RevenueLimitNotification: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state: any) => state.auth);
  const { setting } = useAppSelector((state: any) => state.categoryFormSettings);
  
  const [maxLimitStatus, setMaxLimitStatus] = useState<{
    exceeded: boolean;
    currentTotal: number;
    limit: number;
    daysRemaining: number;
  } | null>(null);
  
  const [minThresholdStatus, setMinThresholdStatus] = useState<{
    reached: boolean;
    currentTotal: number;
    threshold: number;
    daysRemaining: number;
  } | null>(null);

  useEffect(() => {
    const checkRevenueLimits = async () => {
      if (!user?.id || Number(user?.role?.id) !== RoleEnum.professional) {
        return;
      }

      try {
        // Fetch latest settings if not already loaded
        if (!setting) {
          await dispatch(fetchLatestCategoryFormSettings());
        }

        // Check max revenue limit
        const maxLimitResult = await checkMaxRevenueLimit(user.id, setting);
        setMaxLimitStatus(maxLimitResult);

        // Check min revenue threshold
        const minThresholdResult = await checkMinRevenueThreshold(user.id, setting);
        setMinThresholdStatus(minThresholdResult);
      } catch (error) {
        console.error("Error checking revenue limits:", error);
      }
    };

    checkRevenueLimits();
  }, [user?.id, user?.role?.id, setting, dispatch]);

  // Don't render if not a professional
  if (Number(user?.role?.id) !== RoleEnum.professional) {
    return null;
  }

  // Show max revenue limit exceeded notification
  if (maxLimitStatus?.exceeded) {
    return (
      <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg z-50 max-w-md">
        <div className="flex items-center">
          <div className="flex-1">
            <h4 className="font-bold">Maximum Revenue Limit Reached</h4>
            <p className="text-sm">
              You have reached your maximum revenue limit of ${maxLimitStatus.limit.toFixed(2)} 
              with ${maxLimitStatus.currentTotal.toFixed(2)} in the last 365 days.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show min revenue threshold reached notification
  if (minThresholdStatus?.reached) {
    return (
      <div className="fixed top-4 right-4 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded shadow-lg z-50 max-w-md">
        <div className="flex items-center">
          <div className="flex-1">
            <h4 className="font-bold">Approaching Revenue Limit</h4>
            <p className="text-sm">
              You are approaching your revenue limit. Current: ${minThresholdStatus.currentTotal.toFixed(2)} 
              / Threshold: ${minThresholdStatus.threshold.toFixed(2)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default RevenueLimitNotification;
